// ** React Imports
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

/**
 * LocationContext
 * 
 * Provides location data and management across the app
 * - Gets user's current location via geolocation API
 * - Allows manual location selection
 * - Reverse geocodes coordinates to address
 * - Shares location state with all components
 */

const defaultProvider = {
  location: null,
  locationName: '',
  isLoading: true,
  setLocation: () => null,
  setLocationName: () => null,
  refreshLocation: () => Promise.resolve(),
};

const LocationContext = createContext(defaultProvider);
const IP_LOCATION_URL = process.env.REACT_APP_LOCATION_URL || 'https://ipapi.co/json/';
const GEO_OPTIONS = { timeout: 10000, maximumAge: 0, enableHighAccuracy: true };

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const reverseGeocode = async (lat, lng) => {
    try {
      const { data } = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const a = data?.address || {};
      return a.city || a.town || a.village || a.county || 'unknown location';
    } catch {
      return 'unknown location';
    }
  };

  const applyLocation = async (lat, lng) => {
    setLocation({ lat, lng });
    setLocationName(await reverseGeocode(lat, lng));
    setIsLoading(false);
  };

  const fetchLocationFromIP = async () => {
    try {
      const data = await (await fetch(IP_LOCATION_URL)).json();
      const lat = data?.latitude ?? data?.location?.latitude;
      const lng = data?.longitude ?? data?.location?.longitude;
      if (lat != null && lng != null) return await applyLocation(lat, lng);
    } catch (_) {}
    setLocation(null);
    setLocationName('');
    setIsLoading(false);
  };

  const getCurrentLocation = () => {
    setIsLoading(true);
    if (!navigator.geolocation) return fetchLocationFromIP();
    navigator.geolocation.getCurrentPosition(
      (p) => applyLocation(p.coords.latitude, p.coords.longitude),
      fetchLocationFromIP,
      GEO_OPTIONS
    );
  };

  const updateLocation = async (lat, lng) => {
    setIsLoading(true);
    setLocation({ lat, lng });
    setLocationName(await reverseGeocode(lat, lng));
    setIsLoading(false);
  };

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const values = {
    location,
    locationName,
    isLoading,
    setLocation: updateLocation,
    setLocationName,
    refreshLocation: getCurrentLocation,
  };

  return (
    <LocationContext.Provider value={values}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export default LocationContext;


